import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { MessageSquare, Star, Search, ThumbsUp, ThumbsDown, X, Users, Briefcase, Smartphone, Globe, Filter } from 'lucide-react';
import LoadingSpinner from '../../../components/LoadingSpinner';

type FeedbackType = 'all' | 'customer' | 'provider' | 'app' | 'website';

export default function UnifiedFeedbackView() {
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFeedback, setSelectedFeedback] = useState<any>(null);
  const [activeFilter, setActiveFilter] = useState<FeedbackType>('all');

  useEffect(() => {
    loadFeedback();
  }, []);

  const loadFeedback = async () => {
    try {
      setLoading(true);
      
      // Load ALL feedback collections (try all possible collection names)
      const [
        customerFeedbackSnap,
        providerFeedbackSnap,
        platformFeedbacksSnap,
        platformFeedbackSnap,
        serviceProviderReviewsSnap
      ] = await Promise.all([
        getDocs(collection(db, 'customerFeedbacks')),
        getDocs(collection(db, 'feedback')),
        getDocs(collection(db, 'platformFeedbacks')).catch(() => ({ docs: [] })),
        getDocs(collection(db, 'platformFeedback')).catch(() => ({ docs: [] })),
        getDocs(collection(db, 'service_provider_reviews')).catch(() => ({ docs: [] }))
      ]);

      const now = new Date().getTime();
      const oneDayInMs = 24 * 60 * 60 * 1000;

      console.log('Loading feedback from all collections...');
      console.log('Customer Feedbacks:', customerFeedbackSnap.docs.length);
      console.log('Provider Feedbacks:', providerFeedbackSnap.docs.length);
      console.log('Platform Feedbacks (plural):', platformFeedbacksSnap.docs.length);
      console.log('Platform Feedback (singular):', platformFeedbackSnap.docs.length);
      console.log('Service Provider Reviews:', serviceProviderReviewsSnap.docs.length);

      // Process customer feedback (from completed orders)
      const customerFeedback = customerFeedbackSnap.docs.map(doc => {
        const data = doc.data();
        const createdAt = new Date(data.createdAt || 0).getTime();
        const isNew = (now - createdAt) < oneDayInMs;
        
        return {
          id: doc.id,
          type: 'customer',
          collectionName: 'customerFeedbacks',
          ...data,
          isNew,
          displayName: data.userName || data.customerName || 'Anonymous',
          userEmail: data.userEmail || data.email || 'N/A',
          userId: data.userId || 'N/A',
          orderId: data.orderId || 'N/A',
          serviceProviderId: data.serviceProviderId || data.providerId || 'N/A',
          title: data.serviceName || 'Service Feedback',
          category: 'Customer Feedback',
          // Complete rating details
          overallRating: data.ratings?.overall || data.overallRating || 0,
          serviceQuality: data.ratings?.serviceQuality || data.serviceQuality || 0,
          professionalism: data.ratings?.professionalism || data.professionalismRating || 0,
          punctuality: data.ratings?.punctuality || data.punctualityRating || 0,
          valueForMoney: data.ratings?.valueForMoney || data.valueForMoney || 0,
          // Feedback content
          positivePoints: data.positivePoints || '',
          negativePoints: data.negativePoints || '',
          wouldRecommend: data.wouldRecommend ?? null,
          wouldBookAgain: data.wouldBookAgain ?? null
        };
      });

      // Process provider feedback (private feedback from users)
      const providerFeedback = providerFeedbackSnap.docs.map(doc => {
        const data = doc.data();
        const createdAt = new Date(data.createdAt || 0).getTime();
        const isNew = (now - createdAt) < oneDayInMs;
        
        return {
          id: doc.id,
          type: 'provider',
          collectionName: 'feedback',
          ...data,
          isNew,
          displayName: data.userName || 'Anonymous',
          userEmail: data.userEmail || 'N/A',
          userId: data.userId || 'N/A',
          orderId: data.orderId || 'N/A',
          serviceProviderId: data.serviceProviderId || 'N/A',
          title: data.serviceName || 'Provider Feedback',
          category: 'Service Provider Feedback',
          // Complete rating details
          overallRating: data.ratings?.overall || data.overallRating || 0,
          serviceQuality: data.ratings?.serviceQuality || data.serviceQuality || 0,
          professionalism: data.ratings?.professionalism || data.professionalismRating || 0,
          punctuality: data.ratings?.punctuality || data.punctualityRating || 0,
          valueForMoney: data.ratings?.valueForMoney || data.valueForMoney || 0,
          // Feedback content
          positivePoints: data.positivePoints || '',
          negativePoints: data.negativePoints || '',
          wouldRecommend: data.wouldRecommend ?? null,
          wouldBookAgain: data.wouldBookAgain ?? null
        };
      });

      // Process service provider reviews
      const providerReviews = serviceProviderReviewsSnap.docs.map(doc => {
        const data = doc.data();
        const createdAt = new Date(data.createdAt || 0).getTime();
        const isNew = (now - createdAt) < oneDayInMs;
        
        return {
          id: doc.id,
          type: 'provider',
          collectionName: 'service_provider_reviews',
          ...data,
          isNew,
          displayName: data.userName || data.reviewerName || 'Anonymous',
          userEmail: data.userEmail || 'N/A',
          userId: data.userId || 'N/A',
          serviceProviderId: data.serviceProviderId || data.providerId || 'N/A',
          title: 'Service Provider Review',
          category: 'Service Provider Review',
          // Rating details
          overallRating: data.rating || data.overallRating || 0,
          // Feedback content
          review: data.review || data.comment || '',
          positivePoints: data.positivePoints || '',
          negativePoints: data.negativePoints || ''
        };
      });

      // Process platform feedback (app + website) - from both collections
      const appFeedback: any[] = [];
      const websiteFeedback: any[] = [];
      
      // Process platformFeedbacks (plural)
      platformFeedbacksSnap.docs.forEach(doc => {
        const data = doc.data();
        const createdAt = new Date(data.createdAt || data.submittedAt || 0).getTime();
        const isNew = (now - createdAt) < oneDayInMs;
        
        const feedbackItem = {
          id: doc.id,
          collectionName: 'platformFeedbacks',
          ...data,
          isNew,
          displayName: data.name || data.userName || 'Anonymous',
          userEmail: data.email || data.userEmail || 'N/A',
          userId: data.userId || 'N/A',
          feedbackType: data.feedbackType || data.type || 'general',
          feedbackContent: data.feedback || data.message || data.description || '',
          rating: data.rating || 0,
          platform: data.platform || 'unknown',
          deviceInfo: data.deviceInfo || data.device || 'N/A',
          browserInfo: data.browserInfo || data.browser || 'N/A',
          pageUrl: data.pageUrl || data.url || 'N/A',
          screenshot: data.screenshot || null
        };

        if (data.platform === 'mobile' || data.type === 'app' || data.feedbackType === 'app') {
          appFeedback.push({
            ...feedbackItem,
            type: 'app',
            title: `Mobile App - ${data.feedbackType || 'Feedback'}`,
            category: 'Mobile App Feedback'
          });
        } else {
          websiteFeedback.push({
            ...feedbackItem,
            type: 'website',
            title: `Website - ${data.feedbackType || 'Feedback'}`,
            category: 'Website Feedback'
          });
        }
      });

      // Process platformFeedback (singular)
      platformFeedbackSnap.docs.forEach(doc => {
        const data = doc.data();
        const createdAt = new Date(data.createdAt || data.submittedAt || 0).getTime();
        const isNew = (now - createdAt) < oneDayInMs;
        
        const feedbackItem = {
          id: doc.id,
          collectionName: 'platformFeedback',
          ...data,
          isNew,
          displayName: data.name || data.userName || 'Anonymous',
          userEmail: data.email || data.userEmail || 'N/A',
          userId: data.userId || 'N/A',
          feedbackType: data.feedbackType || data.type || 'general',
          feedbackContent: data.feedback || data.message || data.description || '',
          rating: data.rating || 0,
          platform: data.platform || 'unknown',
          deviceInfo: data.deviceInfo || data.device || 'N/A',
          browserInfo: data.browserInfo || data.browser || 'N/A',
          pageUrl: data.pageUrl || data.url || 'N/A',
          screenshot: data.screenshot || null
        };

        if (data.platform === 'mobile' || data.type === 'app' || data.feedbackType === 'app') {
          appFeedback.push({
            ...feedbackItem,
            type: 'app',
            title: `Mobile App - ${data.feedbackType || 'Feedback'}`,
            category: 'Mobile App Feedback'
          });
        } else {
          websiteFeedback.push({
            ...feedbackItem,
            type: 'website',
            title: `Website - ${data.feedbackType || 'Feedback'}`,
            category: 'Website Feedback'
          });
        }
      });

      // Combine all feedback
      const allFeedback = [
        ...customerFeedback,
        ...providerFeedback,
        ...providerReviews,
        ...appFeedback,
        ...websiteFeedback
      ];

      console.log('Total feedback items loaded:', allFeedback.length);
      console.log('- Customer:', customerFeedback.length);
      console.log('- Provider:', providerFeedback.length + providerReviews.length);
      console.log('- App:', appFeedback.length);
      console.log('- Website:', websiteFeedback.length);

      // Sort by created date (newest first)
      allFeedback.sort((a: any, b: any) => {
        const dateA = new Date(a.createdAt || a.submittedAt || 0).getTime();
        const dateB = new Date(b.createdAt || b.submittedAt || 0).getTime();
        return dateB - dateA;
      });

      setFeedback(allFeedback);
    } catch (error) {
      console.error('Error loading feedback:', error);
      alert('Failed to load feedback');
    } finally {
      setLoading(false);
    }
  };

  const filteredFeedback = feedback.filter((item: any) => {
    // Filter by type
    if (activeFilter !== 'all' && item.type !== activeFilter) {
      return false;
    }

    // Filter by search term
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      item.serviceName?.toLowerCase().includes(search) ||
      item.displayName?.toLowerCase().includes(search) ||
      item.userName?.toLowerCase().includes(search) ||
      item.positivePoints?.toLowerCase().includes(search) ||
      item.negativePoints?.toLowerCase().includes(search) ||
      item.feedback?.toLowerCase().includes(search) ||
      item.message?.toLowerCase().includes(search) ||
      item.title?.toLowerCase().includes(search)
    );
  });

  // Calculate stats
  const stats = {
    total: feedback.length,
    customer: feedback.filter(f => f.type === 'customer').length,
    provider: feedback.filter(f => f.type === 'provider').length,
    app: feedback.filter(f => f.type === 'app').length,
    website: feedback.filter(f => f.type === 'website').length,
    new: feedback.filter(f => f.isNew).length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">All Feedback</h1>
          <p className="text-gray-600 mt-1">Unified view of all feedback types</p>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search feedback..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent w-full md:w-80"
          />
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-blue-500">
          <div className="text-2xl font-bold text-blue-600">{stats.total}</div>
          <div className="text-sm text-gray-600">Total</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-green-500">
          <div className="text-2xl font-bold text-green-600">{stats.customer}</div>
          <div className="text-sm text-gray-600">Customer</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-purple-500">
          <div className="text-2xl font-bold text-purple-600">{stats.provider}</div>
          <div className="text-sm text-gray-600">Provider</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-orange-500">
          <div className="text-2xl font-bold text-orange-600">{stats.app}</div>
          <div className="text-sm text-gray-600">App</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-pink-500">
          <div className="text-2xl font-bold text-pink-600">{stats.website}</div>
          <div className="text-sm text-gray-600">Website</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-yellow-500">
          <div className="text-2xl font-bold text-yellow-600">{stats.new}</div>
          <div className="text-sm text-gray-600">New (24h)</div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="bg-white rounded-lg shadow p-2">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setActiveFilter('all')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2 ${
              activeFilter === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Filter className="h-4 w-4" />
            <span>All ({stats.total})</span>
          </button>
          <button
            onClick={() => setActiveFilter('customer')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2 ${
              activeFilter === 'customer'
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Users className="h-4 w-4" />
            <span>Customer ({stats.customer})</span>
          </button>
          <button
            onClick={() => setActiveFilter('provider')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2 ${
              activeFilter === 'provider'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Briefcase className="h-4 w-4" />
            <span>Provider ({stats.provider})</span>
          </button>
          <button
            onClick={() => setActiveFilter('app')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2 ${
              activeFilter === 'app'
                ? 'bg-orange-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Smartphone className="h-4 w-4" />
            <span>App ({stats.app})</span>
          </button>
          <button
            onClick={() => setActiveFilter('website')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2 ${
              activeFilter === 'website'
                ? 'bg-pink-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Globe className="h-4 w-4" />
            <span>Website ({stats.website})</span>
          </button>
        </div>
      </div>

      {/* Feedback List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredFeedback.length === 0 ? (
          <div className="col-span-2 bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <MessageSquare className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No feedback found</h3>
            <p className="text-gray-600">
              {searchTerm ? 'Try adjusting your search terms' : 'No feedback has been submitted yet'}
            </p>
          </div>
        ) : (
          filteredFeedback.map((item: any) => {
            const typeColors = {
              customer: 'border-green-200 hover:border-green-300',
              provider: 'border-purple-200 hover:border-purple-300',
              app: 'border-orange-200 hover:border-orange-300',
              website: 'border-pink-200 hover:border-pink-300'
            };

            const typeBadges = {
              customer: 'bg-green-100 text-green-800',
              provider: 'bg-purple-100 text-purple-800',
              app: 'bg-orange-100 text-orange-800',
              website: 'bg-pink-100 text-pink-800'
            };

            const rating = item.ratings?.overall || item.overallRating || item.rating || 0;

            return (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`bg-white rounded-xl shadow-sm border-2 ${typeColors[item.type as keyof typeof typeColors]} p-6 hover:shadow-lg transition-all cursor-pointer relative`}
                onClick={() => setSelectedFeedback(item)}
              >
                {/* Type Badge */}
                <div className="absolute top-4 right-4 flex items-center gap-2">
                  {item.isNew && (
                    <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-bold rounded-full">
                      NEW
                    </span>
                  )}
                  <span className={`px-3 py-1 ${typeBadges[item.type as keyof typeof typeBadges]} text-xs font-bold rounded-full uppercase`}>
                    {item.type}
                  </span>
                </div>

                {/* Header */}
                <div className="mb-4 pb-4 border-b border-gray-100 pr-32">
                  <h3 className="font-bold text-gray-900 text-lg mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-600 mb-2">{item.displayName}</p>
                  <div className="flex items-center gap-3 text-sm text-gray-500">
                    {item.orderId && (
                      <>
                        <span className="font-mono bg-gray-100 px-2 py-0.5 rounded">#{item.orderId}</span>
                        <span>•</span>
                      </>
                    )}
                    <span>{new Date(item.createdAt || item.submittedAt).toLocaleDateString('en-IN', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric'
                    })}</span>
                  </div>
                </div>

                {/* Rating */}
                {rating > 0 && (
                  <div className="flex items-center gap-2 mb-4">
                    <div className="flex items-center gap-1">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`h-5 w-5 ${
                            i < rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-xl font-bold text-gray-900">{rating.toFixed(1)}</span>
                  </div>
                )}

                {/* Feedback Content */}
                {item.positivePoints && (
                  <div className="bg-green-50 rounded-lg p-3 mb-2 border border-green-200">
                    <p className="text-xs font-bold text-green-800 mb-1 flex items-center gap-1">
                      <ThumbsUp className="h-3.5 w-3.5" />
                      Positive
                    </p>
                    <p className="text-sm text-gray-700 line-clamp-2">{item.positivePoints}</p>
                  </div>
                )}

                {item.negativePoints && (
                  <div className="bg-red-50 rounded-lg p-3 mb-2 border border-red-200">
                    <p className="text-xs font-bold text-red-800 mb-1 flex items-center gap-1">
                      <ThumbsDown className="h-3.5 w-3.5" />
                      Needs Improvement
                    </p>
                    <p className="text-sm text-gray-700 line-clamp-2">{item.negativePoints}</p>
                  </div>
                )}

                {(item.feedback || item.message) && !item.positivePoints && (
                  <div className="bg-gray-50 rounded-lg p-3 mb-2 border border-gray-200">
                    <p className="text-xs font-bold text-gray-800 mb-1 flex items-center gap-1">
                      <MessageSquare className="h-3.5 w-3.5" />
                      Message
                    </p>
                    <p className="text-sm text-gray-700 line-clamp-3">{item.feedback || item.message}</p>
                  </div>
                )}

                {/* Recommendations */}
                {(item.wouldRecommend !== undefined || item.wouldBookAgain !== undefined) && (
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-200">
                    {item.wouldRecommend !== undefined && (
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        item.wouldRecommend ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {item.wouldRecommend ? '👍 Recommends' : '👎 Won\'t Recommend'}
                      </span>
                    )}
                    {item.wouldBookAgain !== undefined && (
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        item.wouldBookAgain ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {item.wouldBookAgain ? '✓ Book Again' : '✗ Won\'t Book'}
                      </span>
                    )}
                  </div>
                )}
              </motion.div>
            );
          })
        )}
      </div>

      {/* Detail Modal */}
      <AnimatePresence>
        {selectedFeedback && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => setSelectedFeedback(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-8"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{selectedFeedback.title}</h2>
                  <p className="text-gray-600 mt-1">{selectedFeedback.category}</p>
                </div>
                <button
                  onClick={() => setSelectedFeedback(null)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="h-6 w-6 text-gray-500" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="space-y-6">
                {/* Complete User/Provider Information */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
                  <h3 className="font-bold text-gray-900 mb-4 text-lg flex items-center gap-2">
                    <Users className="h-5 w-5 text-blue-600" />
                    Complete Information
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white rounded-lg p-3">
                      <p className="text-xs text-gray-500 mb-1">User Name</p>
                      <p className="font-medium text-gray-900">{selectedFeedback.displayName}</p>
                    </div>
                    <div className="bg-white rounded-lg p-3">
                      <p className="text-xs text-gray-500 mb-1">Email</p>
                      <p className="font-medium text-gray-900 break-all">{selectedFeedback.userEmail}</p>
                    </div>
                    <div className="bg-white rounded-lg p-3">
                      <p className="text-xs text-gray-500 mb-1">User ID</p>
                      <p className="font-mono text-xs text-gray-900 break-all">{selectedFeedback.userId}</p>
                    </div>
                    <div className="bg-white rounded-lg p-3">
                      <p className="text-xs text-gray-500 mb-1">Feedback Type</p>
                      <p className="font-medium text-gray-900 capitalize">{selectedFeedback.type}</p>
                    </div>
                    {selectedFeedback.orderId && selectedFeedback.orderId !== 'N/A' && (
                      <div className="bg-white rounded-lg p-3">
                        <p className="text-xs text-gray-500 mb-1">Order ID</p>
                        <p className="font-mono text-sm text-gray-900">#{selectedFeedback.orderId}</p>
                      </div>
                    )}
                    {selectedFeedback.serviceProviderId && selectedFeedback.serviceProviderId !== 'N/A' && (
                      <div className="bg-white rounded-lg p-3">
                        <p className="text-xs text-gray-500 mb-1">Service Provider ID</p>
                        <p className="font-mono text-xs text-gray-900 break-all">{selectedFeedback.serviceProviderId}</p>
                      </div>
                    )}
                    <div className="bg-white rounded-lg p-3">
                      <p className="text-xs text-gray-500 mb-1">Collection</p>
                      <p className="font-mono text-xs text-blue-700">{selectedFeedback.collectionName}</p>
                    </div>
                    <div className="bg-white rounded-lg p-3">
                      <p className="text-xs text-gray-500 mb-1">Submitted Date & Time</p>
                      <p className="font-medium text-sm text-gray-900">
                        {new Date(selectedFeedback.createdAt || selectedFeedback.submittedAt).toLocaleDateString('en-IN', {
                          day: '2-digit',
                          month: 'long',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: true
                        })}
                      </p>
                    </div>
                    {selectedFeedback.platform && (
                      <div className="bg-white rounded-lg p-3">
                        <p className="text-xs text-gray-500 mb-1">Platform</p>
                        <p className="font-medium text-gray-900 capitalize">{selectedFeedback.platform}</p>
                      </div>
                    )}
                    {selectedFeedback.feedbackType && (
                      <div className="bg-white rounded-lg p-3">
                        <p className="text-xs text-gray-500 mb-1">Feedback Category</p>
                        <p className="font-medium text-gray-900 capitalize">{selectedFeedback.feedbackType}</p>
                      </div>
                    )}
                    {selectedFeedback.deviceInfo && selectedFeedback.deviceInfo !== 'N/A' && (
                      <div className="bg-white rounded-lg p-3">
                        <p className="text-xs text-gray-500 mb-1">Device Info</p>
                        <p className="font-medium text-xs text-gray-900">{selectedFeedback.deviceInfo}</p>
                      </div>
                    )}
                    {selectedFeedback.browserInfo && selectedFeedback.browserInfo !== 'N/A' && (
                      <div className="bg-white rounded-lg p-3">
                        <p className="text-xs text-gray-500 mb-1">Browser Info</p>
                        <p className="font-medium text-xs text-gray-900">{selectedFeedback.browserInfo}</p>
                      </div>
                    )}
                    {selectedFeedback.pageUrl && selectedFeedback.pageUrl !== 'N/A' && (
                      <div className="bg-white rounded-lg p-3 col-span-2">
                        <p className="text-xs text-gray-500 mb-1">Page URL</p>
                        <p className="font-mono text-xs text-blue-600 break-all">{selectedFeedback.pageUrl}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Complete Rating Details */}
                {(selectedFeedback.overallRating > 0 || selectedFeedback.serviceQuality > 0 || selectedFeedback.rating > 0) && (
                  <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-lg p-6 border border-yellow-200">
                    <h3 className="font-bold text-gray-900 mb-4 text-lg flex items-center gap-2">
                      <Star className="h-5 w-5 text-yellow-600 fill-yellow-600" />
                      Complete Rating Breakdown
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      {selectedFeedback.overallRating > 0 && (
                        <div className="bg-white rounded-lg p-4 border-2 border-yellow-300">
                          <p className="text-sm text-gray-600 mb-2 font-semibold">Overall Rating</p>
                          <div className="flex items-center gap-2">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`h-6 w-6 ${
                                  i < selectedFeedback.overallRating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
                                }`}
                              />
                            ))}
                            <span className="font-bold text-2xl text-yellow-600">{selectedFeedback.overallRating.toFixed(1)}</span>
                          </div>
                        </div>
                      )}
                      {selectedFeedback.serviceQuality > 0 && (
                        <div className="bg-white rounded-lg p-4">
                          <p className="text-sm text-gray-600 mb-2">Service Quality</p>
                          <div className="flex items-center gap-2">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`h-5 w-5 ${
                                  i < selectedFeedback.serviceQuality ? 'text-blue-400 fill-blue-400' : 'text-gray-300'
                                }`}
                              />
                            ))}
                            <span className="font-bold text-lg">{selectedFeedback.serviceQuality.toFixed(1)}</span>
                          </div>
                        </div>
                      )}
                      {selectedFeedback.professionalism > 0 && (
                        <div className="bg-white rounded-lg p-4">
                          <p className="text-sm text-gray-600 mb-2">Professionalism</p>
                          <div className="flex items-center gap-2">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`h-5 w-5 ${
                                  i < selectedFeedback.professionalism ? 'text-purple-400 fill-purple-400' : 'text-gray-300'
                                }`}
                              />
                            ))}
                            <span className="font-bold text-lg">{selectedFeedback.professionalism.toFixed(1)}</span>
                          </div>
                        </div>
                      )}
                      {selectedFeedback.punctuality > 0 && (
                        <div className="bg-white rounded-lg p-4">
                          <p className="text-sm text-gray-600 mb-2">Punctuality</p>
                          <div className="flex items-center gap-2">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`h-5 w-5 ${
                                  i < selectedFeedback.punctuality ? 'text-green-400 fill-green-400' : 'text-gray-300'
                                }`}
                              />
                            ))}
                            <span className="font-bold text-lg">{selectedFeedback.punctuality.toFixed(1)}</span>
                          </div>
                        </div>
                      )}
                      {selectedFeedback.valueForMoney > 0 && (
                        <div className="bg-white rounded-lg p-4">
                          <p className="text-sm text-gray-600 mb-2">Value for Money</p>
                          <div className="flex items-center gap-2">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`h-5 w-5 ${
                                  i < selectedFeedback.valueForMoney ? 'text-orange-400 fill-orange-400' : 'text-gray-300'
                                }`}
                              />
                            ))}
                            <span className="font-bold text-lg">{selectedFeedback.valueForMoney.toFixed(1)}</span>
                          </div>
                        </div>
                      )}
                      {selectedFeedback.rating > 0 && !selectedFeedback.overallRating && (
                        <div className="bg-white rounded-lg p-4 border-2 border-yellow-300 col-span-2">
                          <p className="text-sm text-gray-600 mb-2 font-semibold">Platform Rating</p>
                          <div className="flex items-center gap-2">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`h-6 w-6 ${
                                  i < selectedFeedback.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
                                }`}
                              />
                            ))}
                            <span className="font-bold text-2xl text-yellow-600">{selectedFeedback.rating.toFixed(1)}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Complete Feedback Content */}
                <div className="space-y-4">
                  {selectedFeedback.positivePoints && (
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-6 border-2 border-green-300">
                      <h3 className="font-bold text-green-900 mb-3 flex items-center gap-2 text-lg">
                        <ThumbsUp className="h-6 w-6" />
                        What Went Well (Positive Points)
                      </h3>
                      <p className="text-gray-800 leading-relaxed text-base whitespace-pre-wrap">{selectedFeedback.positivePoints}</p>
                    </div>
                  )}

                  {selectedFeedback.negativePoints && (
                    <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-lg p-6 border-2 border-red-300">
                      <h3 className="font-bold text-red-900 mb-3 flex items-center gap-2 text-lg">
                        <ThumbsDown className="h-6 w-6" />
                        Areas for Improvement (Negative Points)
                      </h3>
                      <p className="text-gray-800 leading-relaxed text-base whitespace-pre-wrap">{selectedFeedback.negativePoints}</p>
                    </div>
                  )}

                  {selectedFeedback.review && (
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border-2 border-blue-300">
                      <h3 className="font-bold text-blue-900 mb-3 flex items-center gap-2 text-lg">
                        <MessageSquare className="h-6 w-6" />
                        Review / Comments
                      </h3>
                      <p className="text-gray-800 leading-relaxed text-base whitespace-pre-wrap">{selectedFeedback.review}</p>
                    </div>
                  )}

                  {selectedFeedback.feedbackContent && (
                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-6 border-2 border-purple-300">
                      <h3 className="font-bold text-purple-900 mb-3 flex items-center gap-2 text-lg">
                        <MessageSquare className="h-6 w-6" />
                        Platform Feedback Message
                      </h3>
                      <p className="text-gray-800 leading-relaxed text-base whitespace-pre-wrap">{selectedFeedback.feedbackContent}</p>
                    </div>
                  )}

                  {(selectedFeedback.feedback || selectedFeedback.message) && !selectedFeedback.feedbackContent && (
                    <div className="bg-gradient-to-r from-gray-50 to-slate-50 rounded-lg p-6 border-2 border-gray-300">
                      <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2 text-lg">
                        <MessageSquare className="h-6 w-6" />
                        Feedback Message
                      </h3>
                      <p className="text-gray-800 leading-relaxed text-base whitespace-pre-wrap">{selectedFeedback.feedback || selectedFeedback.message}</p>
                    </div>
                  )}

                  {selectedFeedback.description && (
                    <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg p-6 border-2 border-amber-300">
                      <h3 className="font-bold text-amber-900 mb-3 flex items-center gap-2 text-lg">
                        <MessageSquare className="h-6 w-6" />
                        Description
                      </h3>
                      <p className="text-gray-800 leading-relaxed text-base whitespace-pre-wrap">{selectedFeedback.description}</p>
                    </div>
                  )}

                  {selectedFeedback.screenshot && (
                    <div className="bg-white rounded-lg p-6 border-2 border-gray-300">
                      <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2 text-lg">
                        📸 Screenshot Attached
                      </h3>
                      <img 
                        src={selectedFeedback.screenshot} 
                        alt="Feedback Screenshot" 
                        className="rounded-lg border border-gray-300 max-w-full h-auto"
                      />
                    </div>
                  )}
                </div>

                {/* Recommendations */}
                {(selectedFeedback.wouldRecommend !== undefined || selectedFeedback.wouldBookAgain !== undefined) && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-3">Recommendations</h3>
                    <div className="flex gap-3">
                      {selectedFeedback.wouldRecommend !== undefined && (
                        <div className={`flex-1 rounded-lg p-3 ${
                          selectedFeedback.wouldRecommend ? 'bg-green-100 border border-green-300' : 'bg-red-100 border border-red-300'
                        }`}>
                          <p className="text-sm font-medium">Would Recommend</p>
                          <p className="text-lg font-bold">
                            {selectedFeedback.wouldRecommend ? '✅ Yes' : '❌ No'}
                          </p>
                        </div>
                      )}
                      {selectedFeedback.wouldBookAgain !== undefined && (
                        <div className={`flex-1 rounded-lg p-3 ${
                          selectedFeedback.wouldBookAgain ? 'bg-blue-100 border border-blue-300' : 'bg-gray-200 border border-gray-400'
                        }`}>
                          <p className="text-sm font-medium">Would Book Again</p>
                          <p className="text-lg font-bold">
                            {selectedFeedback.wouldBookAgain ? '✅ Yes' : '❌ No'}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
